import { FormEvent, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { AuthLayout } from "@/components/app/auth/AuthLayout";
import { AppLogo } from "@/components/app/brand/AppLogo";
import { AuthField, CaptchaHandle, CaptchaSlot, PasswordField } from "@/components/app/auth/AuthControls";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SEO } from "@/components/SEO";
import { supabase } from "@/integrations/supabase/client";
import { usePlatformAuth } from "@/hooks/usePlatformAuth";
import { isGeneratedChildMailbox } from "@/lib/module-quick-wins";
import { flushPendingRole, readPendingRole } from "@/lib/auth/pending-role";
import { safeNextPath } from "@/lib/auth/paths";
import { flushPendingConsent } from "@/lib/batch-a/consent-client";
import { PhoneOtpForm } from "@/components/batch-a/PhoneOtpForm";

function rateSeconds(message: string) {
  const match = message.match(/(\d+)/);
  return match ? Number(match[1]) : 60;
}

export default function Auth() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const settings = usePlatformAuth();
  const emailRef = useRef<HTMLInputElement>(null);
  const captchaRef = useRef<CaptchaHandle | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [alert, setAlert] = useState<string | null>(null);
  const [confirmEmail, setConfirmEmail] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaError, setCaptchaError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [handingOff, setHandingOff] = useState(false);
  const destination = useRef("/dashboard");

  useEffect(() => {
    if (params.get("tab") === "signup") {
      const role = params.get("role");
      navigate(role ? `/register?role=${encodeURIComponent(role)}` : "/register", { replace: true });
    }
  }, [navigate, params]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setTimeout(() => setCooldown((value) => value - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  useEffect(() => {
    if (!handingOff) return;
    const timer = window.setTimeout(() => navigate(destination.current, { replace: true }), 600);
    return () => window.clearTimeout(timer);
  }, [handingOff, navigate]);

  const validate = () => {
    const schema = z.object({
      email: z.string().email(t("authUi.invalidEmail")),
      password: z.string().min(1, t("authUi.passwordRequired")),
    });
    const parsed = schema.safeParse({ email, password });
    if (parsed.success) {
      setErrors({});
      return true;
    }
    const next: { email?: string; password?: string } = {};
    parsed.error.issues.forEach((issue) => {
      const key = issue.path[0];
      if (key === "email" || key === "password") next[key] = issue.message;
    });
    setErrors(next);
    if (next.email) emailRef.current?.focus();
    else document.getElementById("signin-password")?.focus();
    return false;
  };

  const failCaptcha = () => {
    captchaRef.current?.reset();
    setCaptchaToken(null);
    setCaptchaError(t("authUi.captchaFailed"));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setAlert(null);
    setConfirmEmail(false);
    if (!validate()) return;
    if (settings.captchaSignIn && !captchaToken) {
      setCaptchaError(t("authUi.captchaRequired"));
      captchaRef.current?.focus();
      return;
    }
    setLoading(true);
    try {
      if (settings.captchaSignIn && captchaToken) {
        const { data, error } = await supabase.functions.invoke("verify-hcaptcha", { body: { token: captchaToken } });
        if (error || !data?.success) {
          failCaptcha();
          return;
        }
      }
      const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      if (error) {
        captchaRef.current?.reset();
        setCaptchaToken(null);
        const message = `${error.message} ${error.code ?? ""}`.toLowerCase();
        if (isGeneratedChildMailbox(email)) setAlert(t("authUi.childManaged"));
        else if (message.includes("not confirmed")) {
          setConfirmEmail(true);
          setAlert(t("authUi.confirmEmail"));
        } else if (message.includes("rate")) {
          setCooldown(rateSeconds(error.message));
          setAlert(t("authUi.rate", { seconds: rateSeconds(error.message) }));
        } else if (message.includes("fetch") || message.includes("network")) setAlert(t("authUi.offline"));
        else if (message.includes("invalid")) setAlert(t("authUi.incorrect"));
        else setAlert(error.message);
        return;
      }
      const { data: sessionData } = await supabase.auth.getSession();
      const signedIn = sessionData.session?.user;
      if (signedIn?.id) await flushPendingConsent(signedIn.id);
      if (signedIn?.email && !signedIn.email_confirmed_at) {
        destination.current = "/confirm-email";
        setHandingOff(true);
        return;
      }
      const pending = readPendingRole();
      const flushed = await flushPendingRole();
      const next = safeNextPath(params.get("next"));
      destination.current = pending && !flushed.ok ? "/role-request" : next ?? (pending ? "/pending-approval" : "/dashboard");
      setHandingOff(true);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    const { error } = await supabase.auth.resend({ type: "signup", email: email.trim() });
    if (error) setAlert(error.message);
    setCooldown(60);
  };

  const signInWithGoogle = async () => {
    setAlert(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth`,
        scopes: "openid email profile",
        queryParams: { prompt: "consent", access_type: "offline" },
      },
    });
    if (error) setAlert(error.message);
  };

  if (handingOff) {
    return (
      <div className="grid min-h-dvh place-items-center bg-[#0F1115] text-[#F9FAFB]" aria-busy="true">
        <div className="flex flex-col items-center gap-4">
          <AppLogo variant="mark" size={72} surface="dark" />
          <div className="h-[3px] w-24 bg-white/20">
            <div className="h-full w-1/2 bg-primary motion-safe:animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <AuthLayout>
      <SEO title="Login" description="Sign in to FanJago." meta={[{ name: "robots", content: "noindex" }]} />
      <form onSubmit={handleSubmit} aria-busy={loading} className="space-y-5" noValidate>
        <div className="hidden justify-center lg:flex">
          <AppLogo variant="mark" size={40} />
        </div>
        <div className="space-y-1">
          <h1 className="font-heading text-2xl font-bold">{t("authUi.signInTitle")}</h1>
          <p className="text-sm text-muted-foreground">{t("authUi.signInSubtitle")}</p>
        </div>
        <AuthField id="signin-email" label={t("authUi.email")} error={errors.email}>
          <Input
            ref={emailRef}
            id="signin-email"
            type="email"
            inputMode="email"
            autoCapitalize="none"
            autoComplete="username email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            onBlur={() => {
              if (email && !z.string().email().safeParse(email).success) {
                setErrors((current) => ({ ...current, email: t("authUi.invalidEmail") }));
              }
            }}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? "signin-email-error" : undefined}
            className="h-12 text-base"
          />
        </AuthField>
        <PasswordField
          id="signin-password"
          label={t("authUi.password")}
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          error={errors.password}
          touched
          showRules={false}
          trailing={
            <Link to="/auth/forgot" className="text-sm font-medium text-foreground underline-offset-4 hover:underline">
              {t("authUi.forgot")}
            </Link>
          }
        />
        {alert && (
          <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            <p>{cooldown > 0 && alert === t("authUi.rate", { seconds: cooldown }) ? t("authUi.rate", { seconds: cooldown }) : alert}</p>
            {confirmEmail && (
              <button type="button" className="mt-2 underline" onClick={resend} disabled={cooldown > 0}>
                {cooldown > 0 ? t("authUi.resendIn", { time: `0:${String(cooldown).padStart(2, "0")}` }) : t("authUi.resendEmail")}
              </button>
            )}
          </div>
        )}
        <CaptchaSlot
          enabled={settings.captchaSignIn}
          siteKey={settings.captchaSiteKey}
          onToken={setCaptchaToken}
          error={captchaError}
          handleRef={captchaRef}
        />
        <Button type="submit" className="h-12 w-full text-base" disabled={loading}>
          {loading ? t("authUi.signingIn") : t("authUi.signIn")}
        </Button>
        {settings.googleEnabled && (
          <>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <span className="h-px flex-1 bg-border" />
              {t("authUi.or")}
              <span className="h-px flex-1 bg-border" />
            </div>
            <p className="text-xs text-muted-foreground">{t("modules.batchA.googleNote")}</p>
            <Button type="button" variant="outline" className="h-12 w-full text-base" onClick={signInWithGoogle}>
              {t("authUi.google")}
            </Button>
          </>
        )}
        <PhoneOtpForm />
        <p className="text-sm text-muted-foreground">
          {t("authUi.newTo")}{" "}
          <Link to="/register" className="font-medium text-foreground underline-offset-4 hover:underline">
            {t("authUi.createAccount")}
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
